import { base64ToBlob } from './base64Blob';

export type ReportFileFromPayload = {
  base64: string;
  contentType: string;
  fileName?: string;
};

const BASE64_KEYS = [
  'base64_content',
  'base64Content',
  'file_content',
  'fileContent',
  'content',
  'pdf',
  'base64',
  'file',
  'pdfContent',
] as const;

const CONTENT_TYPE_KEYS = ['content_type', 'contentType'] as const;
const FILE_NAME_KEYS = ['file_name', 'fileName'] as const;

function pickString(obj: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

function guessContentTypeFromFileName(fileName: string | null | undefined): string | null {
  if (!fileName) return null;
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.xlsx')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  if (lower.endsWith('.xls')) return 'application/vnd.ms-excel';
  return null;
}

function guessContentTypeFromBase64(base64: string): string {
  const t = base64.replace(/\s/g, '');
  if (t.startsWith('JVBERi')) return 'application/pdf';
  if (t.startsWith('UEsDB')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  return 'application/octet-stream';
}

function extractReportFileFromObject(obj: Record<string, unknown>): ReportFileFromPayload | null {
  const base64 = pickString(obj, BASE64_KEYS);
  if (!base64) return null;
  const contentType =
    pickString(obj, CONTENT_TYPE_KEYS) ??
    guessContentTypeFromFileName(pickString(obj, FILE_NAME_KEYS)) ??
    'application/octet-stream';
  const fileName = pickString(obj, FILE_NAME_KEYS) ?? undefined;
  return { base64, contentType, fileName };
}

/** Pull base64 file content from API JSON (envelope or nested `data` fields). */
export function extractReportFileFromPayload(body: unknown): ReportFileFromPayload | null {
  if (body == null) return null;
  if (typeof body === 'string') {
    const t = body.trim();
    return t ? { base64: t, contentType: guessContentTypeFromBase64(t) } : null;
  }
  if (typeof body !== 'object') return null;

  const b = body as Record<string, unknown>;
  if (isApiEnvelopeLogicalError(b)) return null;

  const data = b.data;
  if (typeof data === 'string') {
    const t = data.trim();
    if (t) return { base64: t, contentType: guessContentTypeFromBase64(t) };
  }

  if (data && typeof data === 'object') {
    const fromData = extractReportFileFromObject(data as Record<string, unknown>);
    if (fromData) return fromData;
  }

  return extractReportFileFromObject(b);
}

function isApiEnvelopeLogicalError(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  const apiStatus = b.api_status;
  if (!apiStatus || typeof apiStatus !== 'object') return false;
  const as = apiStatus as Record<string, unknown>;
  const errs = as.error;
  if (Array.isArray(errs) && errs.length > 0) return true;
  const st = as.status;
  if (typeof st !== 'string' || !st.trim()) return false;
  return st.trim().toUpperCase() !== 'SUCCESS';
}

function apiEnvelopeErrorMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const apiStatus = b.api_status;
  if (!apiStatus || typeof apiStatus !== 'object') return null;
  const as = apiStatus as Record<string, unknown>;
  const errs = as.error;
  if (Array.isArray(errs) && errs.length) {
    const first = errs[0] as Record<string, unknown>;
    const msg = first?.errorMessage;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  const meta = b.meta && typeof b.meta === 'object' ? (b.meta as Record<string, unknown>) : null;
  const metaMsg = meta?.message;
  if (typeof metaMsg === 'string' && metaMsg.trim()) return metaMsg.trim();
  return null;
}

/** Pull base64 PDF content from API JSON (envelope or nested fields). */
export function extractPdfBase64FromPayload(body: unknown): string | null {
  const file = extractReportFileFromPayload(body);
  return file?.base64 ?? null;
}

function isPdfBytes(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
}

function looksLikeBase64(text: string): boolean {
  const t = text.replace(/\s/g, '');
  return t.length > 64 && /^[A-Za-z0-9+/=]+$/.test(t);
}

function isXlsxBytes(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

/** Turn report export responses (binary, JSON base64, or plain string) into a downloadable Blob. */
export function reportBlobFromResponseBody(
  buffer: ArrayBuffer,
  contentType: string,
): { blob: Blob; fileName?: string } {
  const ct = contentType.toLowerCase();
  const bytes = new Uint8Array(buffer);

  if (ct.includes('application/pdf') || isPdfBytes(bytes)) {
    return { blob: new Blob([buffer], { type: 'application/pdf' }) };
  }

  if (ct.includes('spreadsheetml') || ct.includes('excel') || ct.includes('sheet') || isXlsxBytes(bytes)) {
    return {
      blob: new Blob([buffer], {
        type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
    };
  }

  const text = new TextDecoder().decode(buffer).trim();
  if (!text) {
    throw new Error('Report file response is empty.');
  }

  if (text.startsWith('{') || text.startsWith('[')) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      throw new Error('Could not parse report export response.');
    }

    if (isApiEnvelopeLogicalError(parsed)) {
      throw new Error(apiEnvelopeErrorMessage(parsed) ?? 'Report export failed.');
    }

    const file = extractReportFileFromPayload(parsed);
    if (!file) {
      throw new Error('Report file content missing from response.');
    }

    return {
      blob: base64ToBlob(file.base64, file.contentType),
      fileName: file.fileName,
    };
  }

  if (looksLikeBase64(text)) {
    return { blob: base64ToBlob(text, 'application/octet-stream') };
  }

  throw new Error('Unrecognized report export response format.');
}

/** Turn CMS cover-note PDF responses (binary, JSON base64, or plain string) into a PDF Blob. */
export function pdfBlobFromResponseBody(buffer: ArrayBuffer, contentType: string): Blob {
  const result = reportBlobFromResponseBody(buffer, contentType);
  return result.blob;
}
